import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ConsentManager from './consent/ConsentManager';

const PrivacyPolicy: React.FC = () => (
  <>
  <Helmet>
    <title>Privacy Policy — WeaveWords</title>
    <meta name="description" content="WeaveWords privacy policy — how we collect, use, and protect your data." />
    <meta name="robots" content="index, follow" />
    <link rel="canonical" href="https://www.weavewords.org/privacy/" />
  </Helmet>
  <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px', color: 'var(--text-dark, #333)', lineHeight: 1.7 }}>
    <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>Privacy Policy</h1>
    <p style={{ color: 'var(--text-secondary, #666)', marginBottom: 40 }}>Last updated: June 2025</p>

    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>1. Who we are</h2>
      <p>
        Weavewords (<a href="https://weavewords.org" style={{ color: 'var(--primary-color, #4D8FEB)' }}>weavewords.org</a>) is
        a language-learning application that uses spaced repetition to help you build vocabulary.
        We are the data controller for personal data collected through this service.
      </p>
    </section>

    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>2. Data we collect</h2>
      <ul style={{ paddingLeft: 24 }}>
        <li><strong>Account data</strong> — email address, display name, avatar (required to use the service).</li>
        <li><strong>Learning data</strong> — vocabulary items, collections, spaced repetition records.</li>
        <li><strong>Analytics data</strong> — page views, feature usage, session length (only with your consent).</li>
        <li><strong>Technical data</strong> — IP address (used once to detect your region, not stored).</li>
      </ul>
    </section>

    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>3. How we use your data</h2>
      <ul style={{ paddingLeft: 24 }}>
        <li>To provide and improve the Weavewords service.</li>
        <li>To send you practice reminders you have opted into.</li>
        <li>To understand how the app is used and fix issues (analytics, with consent).</li>
        <li>To process payments for Pro subscriptions via RevenueCat.</li>
      </ul>
    </section>

    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>4. Cookies &amp; tracking</h2>
      <p>
        We use <strong>essential cookies</strong> (authentication, session) that are always active.
        We optionally use <strong>Google Analytics</strong> (analytics cookies) and <strong>Google Ads</strong> (marketing cookies)
        — these only fire after you give consent. All third-party tracking uses Google Consent Mode v2,
        meaning no identifiable data is collected before consent is granted.
      </p>
      <p>
        When you pick a language on weavewords.org, your browser's local storage remembers it so the site
        opens in that language next time. With <strong>preferences</strong> consent, the app also sets a
        <code> ww_signed_in</code> cookie on weavewords.org that only says you are signed in (no account
        details), so the website can show "Open app" instead of "Log in".
      </p>
    </section>

    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>5. Your rights (GDPR)</h2>
      <p>If you are in the EU/EEA you have the right to:</p>
      <ul style={{ paddingLeft: 24 }}>
        <li>Access, correct, or delete your personal data.</li>
        <li>Withdraw consent for analytics/marketing at any time (see below).</li>
        <li>Data portability — export your vocabulary as CSV.</li>
        <li>Lodge a complaint with your national data protection authority.</li>
      </ul>
      <p>To exercise these rights, contact us at <a href="mailto:admin@weavewords.org" style={{ color: 'var(--primary-color, #4D8FEB)' }}>admin@weavewords.org</a>.</p>
    </section>

    <section style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>6. Third-party services</h2>
      <ul style={{ paddingLeft: 24 }}>
        <li><strong>Google Analytics</strong> — usage analytics (consent required in EU).</li>
        <li><strong>RevenueCat</strong> — payment processing for Pro subscriptions.</li>
        <li><strong>Amazon S3</strong> — storage for uploaded images.</li>
      </ul>
    </section>

    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>7. Contact</h2>
      <p>
        Questions about this policy? Email{' '}
        <a href="mailto:admin@weavewords.org" style={{ color: 'var(--primary-color, #4D8FEB)' }}>admin@weavewords.org</a>.
      </p>
    </section>

    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Cookie preferences</h2>
      <ConsentManager />
    </section>

    <p style={{ fontSize: 13, color: 'var(--text-secondary, #666)' }}>
      <Link to="/" style={{ color: 'var(--primary-color, #4D8FEB)' }}>← Back to Weavewords</Link>
    </p>
  </div>
  </>
);

export default PrivacyPolicy;
